import { ArrowUpRight, Newspaper } from "lucide-react";
import { Reveal } from "./Reveal";

const FEATURED = {
  source: "Bucks County Herald",
  date: "August 2026",
  tag: "Milestone",
  title: "Doylestown clears the way for Adam's Way",
  body: "The borough zoning hearing board granted a key variance for the inclusive community at 180–190 S Clinton Street. \"A major step forward,\" said Executive Director Josh Fields, with land development, permitting, and financing phases ahead.",
  url: "https://www.buckscountyherald.com/news/the-next-step-programs-secures-zoning-approval-for-parking-at-adam-s-way/article_ecf8ebce-e51b-4649-9fd0-98f839e694a2.html",
  image: "/photos/adams-way-rendering.jpg",
};

const ARTICLES = [
  {
    source: "Hoodline",
    date: "May 2026",
    title: "Inside Adam's Way: Doylestown's bold new bet on disability housing",
    body: "A deep look at the $25 million vision: 41 inclusive homes above a public marketplace, and the community campaign crossing its first million.",
    url: "https://hoodline.com/2026/05/inside-adam-s-way-doylestown-s-bold-new-bet-on-disability-housing/",
  },
  {
    source: "NBC10 Philadelphia",
    date: "April 2026",
    title: "Helping Those in Need with The Next Step Programs",
    body: "Executive Director Josh Fields sits down with NBC10 to share the story behind Adam's Way and the neighbors it will serve. Watch the segment on the project site.",
    url: "https://adamswaypa.org",
  },
  {
    source: "Bucks County Herald",
    date: "April 2026",
    title: "TNS showcases Adam's Way concept in Doylestown Borough",
    body: "Conceptual renderings created with Union Studio Architecture & Community Design unveiled to the community for the first time.",
    url: "https://www.buckscountyherald.com/news/the-next-step-programs-showcases-adams-way-concept-in-doylestown-borough/article_25956758-b5f9-4463-b0eb-b876c460ae6d.html",
  },
  {
    source: "Patch",
    date: "February 2026",
    title: "Adam's Way takes big step forward with acquisition of Doylestown property",
    body: "The 2.5-acre former Paul B. Moyer site on South Clinton Street officially becomes the future home of Adam's Way.",
    url: "https://patch.com/pennsylvania/doylestown/adams-way-takes-big-step-forward-acquisition-doylestown-property",
  },
  {
    source: "Bucks County Herald",
    date: "February 2026",
    title: "The Next Step Programs gets Doylestown real estate deal done",
    body: "TNS Housing Corp finalizes the purchase of the downtown property where the inclusive community will rise.",
    url: "https://www.buckscountyherald.com/news/the-next-step-programs-gets-doylestown-real-estate-deal-done/article_ff55e850-a6f1-4d1c-a5df-94193ee97576.html",
  },
  {
    source: "PhillyBurbs",
    date: "February 2026",
    title: "TNS plans inclusive housing, marketplace for IDD community in Doylestown",
    body: "Coverage of the vision for a mixed-use community where adults with and without disabilities share a building and a neighborhood.",
    url: "https://www.phillyburbs.com/story/news/education/2026/02/27/next-step-programs-disability-services-inclusive-housing-marketplace-for-idd-doylestown-adams-way/88901085007/",
  },
];

const News = () => (
  <section id="news" data-testid="news-section" className="py-28 lg:py-40">
    <div className="max-w-[90rem] mx-auto px-6 lg:px-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14 lg:mb-20">
        <Reveal>
          <p className="overline-label mb-6" data-testid="news-overline">Newsroom</p>
          <h2
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
            data-testid="news-heading"
          >
            Adam's Way <em className="text-primary font-normal">in the news</em>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground" data-testid="news-outlets">
            As featured in · NBC10 · Bucks County Herald · Patch · Hoodline · PhillyBurbs
          </p>
        </Reveal>
      </div>

      <Reveal>
        <a
          href={FEATURED.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="news-featured"
          className="group grid grid-cols-12 gap-8 lg:gap-12 items-center bg-card border border-foreground/10 rounded-3xl overflow-hidden transition-colors duration-500 hover:border-primary/30 mb-14 lg:mb-20"
        >
          <div className="col-span-12 lg:col-span-5 h-full">
            <div className="aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden">
              <img
                src={FEATURED.image}
                alt="Concept rendering of Adam's Way"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7 p-8 lg:p-14 lg:pl-4">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] text-primary font-medium">
                <Newspaper size={12} />
                {FEATURED.source}
              </span>
              <span className="font-serif italic text-base text-muted-foreground">{FEATURED.date}</span>
            </div>
            <h3 className="font-serif font-light text-3xl lg:text-5xl leading-[1.05] tracking-tight transition-colors duration-300 group-hover:text-primary">
              {FEATURED.title}
            </h3>
            <p className="mt-5 text-base lg:text-lg leading-relaxed text-muted-foreground max-w-xl">
              {FEATURED.body}
            </p>
            <span className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-foreground link-line">
              Read the full story
              <ArrowUpRight size={16} className="text-primary transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
          </div>
        </a>
      </Reveal>

      <div>
        {ARTICLES.map((a, i) => (
          <Reveal key={i} delay={Math.min(i * 0.05, 0.2)}>
            <a
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`news-item-${i}`}
              className={`group grid grid-cols-12 gap-6 py-9 lg:py-10 border-t border-foreground/10 -mx-4 px-4 lg:-mx-8 lg:px-8 rounded-2xl transition-colors duration-500 hover:bg-secondary/40 ${
                i === ARTICLES.length - 1 ? "border-b" : ""
              }`}
            >
              <div className="col-span-12 sm:col-span-3 lg:col-span-2">
                <span className="inline-block rounded-full border border-primary/30 px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-primary">
                  {a.source}
                </span>
                <p className="mt-2 font-serif italic text-base text-muted-foreground">{a.date}</p>
              </div>
              <div className="col-span-12 sm:col-span-9 lg:col-span-6">
                <h3 className="font-serif font-light text-2xl lg:text-3xl leading-tight transition-colors duration-300 group-hover:text-primary">
                  {a.title}
                </h3>
              </div>
              <div className="col-span-12 lg:col-span-4 flex items-start justify-between gap-4">
                <p className="text-sm leading-relaxed text-muted-foreground">{a.body}</p>
                <ArrowUpRight
                  size={20}
                  className="shrink-0 text-foreground/30 transition-[color,transform] duration-300 group-hover:text-primary group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default News;
